import type { LocalizedString } from "../i18n";

/**
 * Product-type suitability assessment — a set of questions, each with
 * point-scored options. The client passes a mode when the sum of their
 * selected options' points across the (asked) questions meets the pass score.
 * Every product in the category inherits the category's verdict.
 *
 * Two modes share the same scored-question model:
 * - QUESTIONNAIRE — every question is asked inline and the sum of all answers
 *   is scored (e.g. "number of trades" + "trading volume").
 * - QUIZ — a random subset of `questionsToAsk` questions is drawn per attempt,
 *   with an attempt limit and a day-based cooldown; the "correct" answer is
 *   simply the high-scoring option.
 *
 * A product type may enable one or more modes (at most one per mode); the
 * per-mode statuses combine into the category verdict with AND semantics in
 * `product-assessment-verdict.ts` (the verdict engine). A product type with
 * no assessment falls back to the plain yes/no experience declaration on the
 * flow page.
 *
 * The pass rules live here so the API (scoring service + flow-completion
 * engine) and the web UI (badges, gating) share one definition.
 */

export type ProductTypeAssessmentMode = "QUESTIONNAIRE" | "QUIZ";

/** One point-scored answer option of a question. */
export type ProductTypeAssessmentOption = {
  id: string;
  labelI18n: LocalizedString;
  score: number;
};

export type ProductTypeAssessmentQuestion = {
  id: string;
  labelI18n: LocalizedString;
  /** At least 2 options. */
  options: ProductTypeAssessmentOption[];
  /**
   * QL-253 — a generic, unnamed grouping slot (1-10), or null/absent for
   * "None". QUIZ mode only: once any question in the pool carries a real
   * category, `questionsToAsk` draws that many questions from EACH category
   * present (None included) instead of one flat draw across the whole pool —
   * see `drawQuestions` in `apps/api/src/services/product-assessment-compute.ts`.
   * Purely a draw-composition control; never shown to the person taking the quiz.
   */
  category?: number | null;
};

export type ProductTypeAssessment = {
  id: string;
  productTypeId: string;
  mode: ProductTypeAssessmentMode;
  /** Pass bar — the summed score of the (asked) questions must be >= this. */
  minScore: number;
  questions: ProductTypeAssessmentQuestion[];
  /** QUIZ: number of questions drawn at random per attempt. QUESTIONNAIRE asks all. */
  questionsToAsk: number;
  /** QUIZ: max attempts before cooldown. Null = unlimited. Ignored for QUESTIONNAIRE. */
  maxAttempts: number | null;
  /** QUIZ: days after the last attempt when the counter resets. Null = never. Ignored otherwise. */
  attemptResetDays: number | null;
};

/** One question with option scores stripped (client-safe). */
export type ProductTypeAssessmentPublicQuestion = {
  id: string;
  labelI18n: LocalizedString;
  options: Array<{ id: string; labelI18n: LocalizedString }>;
};

/**
 * Client-safe projection. Option scores are always stripped. For QUESTIONNAIRE
 * the full question set is included so it can render inline; for QUIZ the
 * question set is omitted (questions are drawn per attempt via quiz/start).
 */
export type ProductTypeAssessmentPublic = {
  id: string;
  productTypeId: string;
  /**
   * The product type's name, unresolved — as `labelI18n` is below. Carried here
   * so a caller that has never rendered the flow can map its own categories
   * onto `productTypeId`: this is the only payload that lists them before
   * anything has been answered.
   */
  productTypeNameI18n: LocalizedString;
  mode: ProductTypeAssessmentMode;
  minScore: number;
  questions: ProductTypeAssessmentPublicQuestion[] | null;
  /**
   * QUIZ only. QL-253 — the configured per-category draw count, not the
   * attempt total: once any question carries a real category, an attempt
   * draws this many from EACH category present (None included). Read
   * `questionsPerAttempt` for the actual number of questions an attempt
   * contains.
   */
  questionsToAsk: number;
  /**
   * QUIZ only — null for QUESTIONNAIRE, which asks every question. QL-253 —
   * the real number of questions an attempt contains, computed from
   * `questionsToAsk` and the current categories.
   */
  questionsPerAttempt: number | null;
  maxAttempts: number | null;
  attemptResetDays: number | null;
};

// --- Configured assessment types (QL-361) ---

/**
 * One active product type of a portfolio collection, with its assessment
 * modes. A simple (declaration-only) type has both `quiz` and `questionnaire`
 * null; its only condition source is Simple (QL-541). Inactive types are
 * never listed. Feeds the condition pickers and every evaluator of the
 * `__pa_…` condition sources, so "what is configured" has one definition.
 */
export type ConfiguredAssessmentType = {
  id: string;
  nameI18n: LocalizedString;
  /** Null when the type has no quiz. `passScore` is the mode's pass bar, in points. */
  quiz: { passScore: number } | null;
  /** Null when the type has no questionnaire. */
  questionnaire: { passScore: number } | null;
};

export type ConfiguredAssessmentTypesResponse = ConfiguredAssessmentType[];

/**
 * QL-698 — `GET /public/flow/product-assessment/condition-values`: every
 * configured `__pa_…` source's value, resolved server-side from the full stored
 * record, so a member's session reads the whole group's result. Status strings
 * as they are, scores as "min,max" ("" = no score). Same for every session.
 */
export type ProductAssessmentConditionValuesResponse = { values: Record<string, string> };

// --- CRUD API contract ---

export type UpsertProductTypeAssessmentRequest = {
  mode: ProductTypeAssessmentMode;
  minScore: number;
  questions: ProductTypeAssessmentQuestion[];
  questionsToAsk?: number;
  maxAttempts?: number | null;
  attemptResetDays?: number | null;
};

// --- Pass rules (shared by API engine and web UI) ---

/**
 * Sum of the client's selected option scores across the given questions. An
 * unanswered question (or an unknown option id) contributes 0.
 */
export function sumAssessmentScore(
  questions: Array<{ id: string; options: Array<{ id: string; score: number }> }>,
  selectedOptionByQuestionId: Record<string, string | null | undefined>,
): number {
  let total = 0;
  for (const question of questions) {
    const selectedId = selectedOptionByQuestionId[question.id];
    const option = selectedId ? question.options.find((o) => o.id === selectedId) : undefined;
    total += option?.score ?? 0;
  }
  return total;
}

/**
 * The best score any option of a question gives — the `maxScore` that
 * `answerScores[].answers[]` reports for an answered question, and that the
 * public product-type bank reports for every question. Null when the question
 * has no options: `Math.max()` of nothing is -Infinity, which JSON turns into a
 * null that would read as "question removed".
 */
export function questionMaxScore(
  question: { options: ReadonlyArray<{ score: number }> } | undefined,
): number | null {
  if (!question?.options.length) return null;
  return Math.max(...question.options.map((o) => o.score));
}

/** Pass when the summed score meets the bar. Null (unanswered) never passes. */
export function isAssessmentPassing(
  minScore: number,
  totalScore: number | null | undefined,
): boolean {
  if (totalScore == null) return false;
  return totalScore >= minScore;
}

/** Quiz attempt availability for one investor on one assessment. */
export type QuizAttemptAvailability = {
  canAttempt: boolean;
  /** Attempts left in the current window (null when maxAttempts is null = unlimited). */
  remaining: number | null;
  /** Set when blocked by cooldown: ISO timestamp when attempts reset. */
  cooldownUntil: string | null;
  /** A passed quiz locks permanently — no retakes. */
  locked: boolean;
};

/**
 * Attempt availability rules: a pass locks the quiz forever; below
 * maxAttempts you may attempt; at/over maxAttempts you are blocked until
 * `lastAttemptAt + attemptResetDays` has elapsed, after which the counter
 * resets to a fresh round.
 */
export function getQuizAttemptAvailability(
  state:
    | { attemptsCount: number; lastAttemptAt: string | null; passed: boolean }
    | null
    | undefined,
  maxAttempts: number | null,
  attemptResetDays: number | null,
  now: Date,
): QuizAttemptAvailability {
  if (state?.passed) {
    return { canAttempt: false, remaining: 0, cooldownUntil: null, locked: true };
  }
  if (maxAttempts == null) {
    return { canAttempt: true, remaining: null, cooldownUntil: null, locked: false };
  }
  const attempts = state?.attemptsCount ?? 0;
  const remaining = Math.max(0, maxAttempts - attempts);
  if (remaining > 0) {
    return { canAttempt: true, remaining, cooldownUntil: null, locked: false };
  }
  if (attemptResetDays != null && state?.lastAttemptAt) {
    const resetAt = new Date(state.lastAttemptAt);
    resetAt.setUTCDate(resetAt.getUTCDate() + attemptResetDays);
    if (now >= resetAt) {
      return { canAttempt: true, remaining: maxAttempts, cooldownUntil: null, locked: false };
    }
    return { canAttempt: false, remaining: 0, cooldownUntil: resetAt.toISOString(), locked: false };
  }
  return { canAttempt: false, remaining: 0, cooldownUntil: null, locked: false };
}

/**
 * QL-528 — the counter and `lastAttemptAt` a reader should report for a state:
 * once the limit was reached and `attemptResetDays` has elapsed, the round is
 * fresh (`getQuizAttemptAvailability` says `remaining: maxAttempts`), so the
 * counter reads 0 and there is no last attempt in the window. Derived from the
 * availability rule itself so the two cannot disagree.
 */
export function getQuizAttemptWindow(
  state:
    | { attemptsCount: number; lastAttemptAt: string | null; passed: boolean }
    | null
    | undefined,
  maxAttempts: number | null,
  attemptResetDays: number | null,
  now: Date,
): { attemptsCount: number; lastAttemptAt: string | null } {
  const attemptsCount = state?.attemptsCount ?? 0;
  const lastAttemptAt = state?.lastAttemptAt ?? null;
  const availability = getQuizAttemptAvailability(state, maxAttempts, attemptResetDays, now);
  const expired =
    !availability.locked &&
    maxAttempts != null &&
    attemptsCount >= maxAttempts &&
    availability.canAttempt;
  return expired ? { attemptsCount: 0, lastAttemptAt: null } : { attemptsCount, lastAttemptAt };
}
